import { e2eDashboardData } from "../data/e2eDashboardData";

const statusLabels = {
  passed: "Passed",
  blocked: "Blocked",
  resolved: "Resolved"
};

function statusClassName(status) {
  return `e2e-status e2e-status-${status || "blocked"}`;
}

function countEvidence(runs) {
  return runs.reduce((total, run) => total + (run.evidence?.length || 0), 0);
}

function collectOpenFollowUps(runs) {
  return runs.flatMap((run) => (
    (run.followUps || []).map((detail) => ({
      scenario: run.scenario,
      detail
    }))
  ));
}

export function BuildE2EDashboardPage({ selectedRange }) {
  const runs = e2eDashboardData.runs;
  const passedRuns = runs.filter((run) => run.status === "passed");
  const unresolvedRuns = runs.filter((run) => run.status === "blocked");
  const followUps = collectOpenFollowUps(runs);
  const latestRun = runs[0];

  return (
    <div className="e2e-dashboard">
      <section className="panel e2e-hero-panel">
        <div className="panel-header e2e-hero-header">
          <div>
            <h2>Build and E2E Test Dashboard</h2>
            <p>
              Tracks real owner-flow runs, Stripe readiness, evidence files, and the next tests before deployment.
            </p>
          </div>
          <div className={statusClassName(latestRun.status)}>
            {statusLabels[latestRun.status] || latestRun.status}
          </div>
        </div>

        <div className="e2e-summary-grid">
          <div>
            <span>Total runs logged</span>
            <strong>{runs.length}</strong>
          </div>
          <div>
            <span>Passed runs</span>
            <strong>{passedRuns.length}</strong>
          </div>
          <div>
            <span>Open blockers</span>
            <strong>{unresolvedRuns.length}</strong>
          </div>
          <div>
            <span>Evidence files</span>
            <strong>{countEvidence(runs)}</strong>
          </div>
        </div>

        <div className="e2e-latest">
          <span>Latest verified flow</span>
          <strong>{latestRun.scenario}</strong>
          <p>{latestRun.summary}</p>
        </div>
      </section>

      <section className="panel e2e-context-panel">
        <div className="panel-header">
          <h3>Test Context</h3>
          <p>Current execution assumptions for owner-flow validation.</p>
        </div>
        <div className="e2e-context-grid">
          <div>
            <span>Active range</span>
            <strong>{selectedRange?.name || "No range selected"}</strong>
          </div>
          {e2eDashboardData.environment.map((item) => (
            <div key={item.label}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          ))}
          <div>
            <span>Log source</span>
            <strong>{e2eDashboardData.logSource}</strong>
          </div>
        </div>
      </section>

      <div className="e2e-two-column">
        <section className="panel">
          <div className="panel-header">
            <h3>Next E2E Tests</h3>
            <p>Run these after the Stripe owner setup remains green.</p>
          </div>
          <div className="e2e-next-list">
            {e2eDashboardData.nextTests.map((test) => (
              <article key={test.title}>
                <strong>{test.title}</strong>
                <p>{test.detail}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <h3>Open Follow-Ups</h3>
            <p>Items carried forward from the latest real-user passes.</p>
          </div>
          <div className="e2e-follow-up-list">
            {followUps.length > 0 ? followUps.map((item) => (
              <article key={`${item.scenario}-${item.detail}`}>
                <span>{item.scenario}</span>
                <p>{item.detail}</p>
              </article>
            )) : (
              <article>
                <span>Clear</span>
                <p>No follow-ups are currently logged.</p>
              </article>
            )}
          </div>
        </section>
      </div>

      <section className="panel e2e-runs-panel">
        <div className="panel-header">
          <h3>Logged Runs</h3>
          <p>Each row maps a real scenario to status, evidence, and what was verified.</p>
        </div>
        <div className="e2e-run-list">
          {runs.map((run) => (
            <article className="e2e-run-card" key={run.id}>
              <div className="e2e-run-card-header">
                <div>
                  <span>{run.date} - {run.area} - {run.role}</span>
                  <h3>{run.scenario}</h3>
                </div>
                <div className={statusClassName(run.status)}>
                  {statusLabels[run.status] || run.status}
                </div>
              </div>

              <p>{run.summary}</p>

              <div className="e2e-run-meta">
                <div>
                  <span>Range</span>
                  <strong>{run.range}</strong>
                </div>
                <div>
                  <span>Stripe/account</span>
                  <strong>{run.account}</strong>
                </div>
              </div>

              <div className="e2e-detail-grid">
                <div>
                  <span>Verified</span>
                  <ul>
                    {run.verifications.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <span>Evidence</span>
                  <ul>
                    {run.evidence.map((item) => (
                      <li key={item}>
                        <code>{item}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
